import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Product } from '../types/Product';
import { products } from '../data/products';

const ProductDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('description');
  const [quantity, setQuantity] = useState(1);

  // Simuler une requête API
  useEffect(() => {
    setLoading(true);
    // Simulation d'un délai réseau
    const timer = setTimeout(() => {
      const foundProduct = products.find(p => p.id === id);
      setProduct(foundProduct || null);
      setLoading(false);
    }, 500);

    return () => clearTimeout(timer);
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen pt-20 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl font-bold text-gray-900">Produit non trouvé</h1>
          <p className="mt-2 text-gray-600">Le produit que vous recherchez n'existe pas ou a été retiré.</p>
          <Link to="/catalogue">
            <button className="mt-6 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
              Retour au catalogue
            </button>
          </Link>
        </div>
      </div>
    );
  }

  const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value);
    if (!isNaN(value) && value >= 1) {
      setQuantity(value);
    }
  };

  return (
    <div className="min-h-screen pt-20 pb-12 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Fil d'Ariane */}
        <nav className="flex py-4" aria-label="Breadcrumb">
          <ol className="flex items-center space-x-2">
            <li>
              <Link to="/" className="text-gray-500 hover:text-gray-700">Accueil</Link>
            </li>
            <li className="flex items-center">
              <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
              <Link to="/catalogue" className="ml-2 text-gray-500 hover:text-gray-700">Catalogue</Link>
            </li>
            <li className="flex items-center">
              <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
              <span className="ml-2 text-gray-900 font-medium">{product.name}</span>
            </li>
          </ol>
        </nav>

        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="md:flex">
            <div className="md:w-1/2">
              <motion.div 
                className="h-80 md:h-full relative overflow-hidden"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
              >
                <img 
                  src={product.image} 
                  alt={product.name} 
                  className="w-full h-full object-cover object-center"
                />
                <div className="absolute top-0 left-0 m-4 bg-indigo-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                  {product.category}
                </div>
              </motion.div>
            </div>
            <div className="md:w-1/2 p-8">
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <div className="flex items-center">
                  <div className="flex items-center">
                    {[...Array(5)].map((_, i) => (
                      <svg 
                        key={i} 
                        className={`h-5 w-5 ${i < Math.floor(product.rating) ? 'text-yellow-400' : 'text-gray-300'}`}
                        xmlns="http://www.w3.org/2000/svg" 
                        viewBox="0 0 20 20" 
                        fill="currentColor"
                      >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118l-2.8-2.034c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                    <span className="ml-2 text-sm text-gray-600">{product.rating.toFixed(1)} ({product.reviewCount} avis)</span>
                  </div>
                  <span className="mx-2 text-gray-500">•</span>
                  <span className="text-sm text-gray-600">
                    {product.stock > 0 ? 
                      <span className="text-green-600 font-medium">En stock</span> : 
                      <span className="text-red-600 font-medium">Rupture de stock</span>
                    }
                  </span>
                </div>

                <h1 className="mt-4 text-3xl font-bold text-gray-900">{product.name}</h1>
                
                <div className="mt-6">
                  <p className="text-3xl font-bold text-indigo-600">
                    {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: product.currency }).format(product.price)}
                  </p>
                  <p className="mt-1 text-sm text-gray-500">Pour une commande en gros</p>
                </div>

                <div className="mt-6 space-y-6">
                  <div>
                    <h3 className="text-sm font-medium text-gray-900">Informations clés</h3>
                    <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                      <div className="flex items-center">
                        <svg className="h-5 w-5 text-indigo-500 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                        </svg>
                        <span>MOQ: {product.moq} unités</span>
                      </div>
                      <div className="flex items-center">
                        <svg className="h-5 w-5 text-indigo-500 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>Origine: {product.origin}</span>
                      </div>
                      <div className="flex items-center">
                        <svg className="h-5 w-5 text-indigo-500 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span>Disponibilité: Immédiate</span>
                      </div>
                      <div className="flex items-center">
                        <svg className="h-5 w-5 text-indigo-500 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                        <span>Vendeur certifié</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <div className="flex space-x-4 border-b border-gray-200">
                      <button
                        className={`pb-2 px-1 border-b-2 font-medium text-sm focus:outline-none ${activeTab === 'description' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                        onClick={() => setActiveTab('description')}
                      >
                        Description
                      </button>
                      <button
                        className={`pb-2 px-1 border-b-2 font-medium text-sm focus:outline-none ${activeTab === 'shipping' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                        onClick={() => setActiveTab('shipping')}
                      >
                        Expédition
                      </button>
                      <button
                        className={`pb-2 px-1 border-b-2 font-medium text-sm focus:outline-none ${activeTab === 'seller' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                        onClick={() => setActiveTab('seller')}
                      >
                        Vendeur
                      </button>
                    </div>
                    
                    <div className="mt-4">
                      {activeTab === 'description' && (
                        <p className="text-sm text-gray-700">{product.description}</p>
                      )}
                      
                      {activeTab === 'shipping' && (
                        <div className="text-sm text-gray-700">
                          <p>Livraison disponible vers : {product.destination.join(', ')}</p>
                          <ul className="mt-2 list-disc pl-5 space-y-1">
                            <li>Délai d'expédition estimé : 5-15 jours ouvrables</li>
                            <li>Expédition internationale avec suivi</li>
                            <li>Documents douaniers inclus</li>
                            <li>Assurance transport disponible en option</li>
                          </ul>
                        </div>
                      )}
                      
                      {activeTab === 'seller' && (
                        <div className="text-sm text-gray-700">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center">
                              <span className="text-indigo-800 font-bold">{product.seller.name.charAt(0)}</span>
                            </div>
                            <div className="ml-4">
                              <h4 className="font-medium">{product.seller.name}</h4>
                              <p className="text-gray-500">Basé en {product.seller.country}</p>
                            </div>
                          </div>
                          <div className="mt-3">
                            <p>Vendeur vérifié depuis {new Date(product.seller.verifiedSince).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long' })}</p>
                            <div className="mt-1 flex items-center">
                              <div className="flex items-center">
                                {[...Array(5)].map((_, i) => (
                                  <svg 
                                    key={i} 
                                    className={`h-4 w-4 ${i < Math.floor(product.seller.rating) ? 'text-yellow-400' : 'text-gray-300'}`}
                                    xmlns="http://www.w3.org/2000/svg" 
                                    viewBox="0 0 20 20" 
                                    fill="currentColor"
                                  >
                                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118l-2.8-2.034c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                  </svg>
                                ))}
                                <span className="ml-1 text-gray-600">{product.seller.rating.toFixed(1)}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="mt-8">
                  <div className="flex items-center justify-between mb-4">
                    <label htmlFor="quantity" className="block text-sm font-medium text-gray-700">Quantité</label>
                    <span className="text-sm text-gray-500">Stock disponible: {product.stock}</span>
                  </div>
                  <div className="flex rounded-md shadow-sm">
                    <input
                      type="number"
                      id="quantity"
                      name="quantity"
                      min={product.moq}
                      max={product.stock}
                      value={quantity}
                      onChange={handleQuantityChange}
                      className="flex-1 min-w-0 block w-full px-3 py-2 rounded-none rounded-l-md border-gray-300 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      placeholder="Quantité"
                    />
                    <button
                      type="button"
                      className="inline-flex items-center px-4 py-2 border border-l-0 border-gray-300 text-sm font-medium rounded-r-md text-gray-700 bg-gray-50 hover:bg-gray-100 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                      onClick={() => setQuantity(product.moq)}
                    >
                      MOQ
                    </button>
                  </div>

                  <div className="flex space-x-4 mt-6">
                    <motion.button
                      className="flex-1 bg-indigo-600 py-3 px-8 border border-transparent rounded-md shadow-sm text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      Ajouter au panier
                    </motion.button>
                    <motion.button
                      className="flex-1 bg-white py-3 px-8 border border-gray-300 rounded-md shadow-sm text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      Contacter le vendeur
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Produits similaires */}
        <div className="mt-12">
          <h2 className="text-2xl font-bold text-gray-900">Produits similaires</h2>
          <div className="mt-6 grid grid-cols-1 gap-y-10 gap-x-6 sm:grid-cols-2 lg:grid-cols-4">
            {products
              .filter(p => p.category === product.category && p.id !== product.id)
              .slice(0, 4)
              .map((similarProduct, index) => (
                <motion.div 
                  key={similarProduct.id}
                  className="group relative"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.1 * index }}
                >
                  <div className="w-full min-h-80 bg-gray-200 aspect-w-1 aspect-h-1 rounded-md overflow-hidden group-hover:opacity-75">
                    <img
                      src={similarProduct.image}
                      alt={similarProduct.name}
                      className="w-full h-full object-center object-cover"
                    />
                  </div>
                  <div className="mt-4 flex justify-between">
                    <div>
                      <h3 className="text-sm text-gray-700">
                        <Link to={`/produit/${similarProduct.id}`}>
                          <span aria-hidden="true" className="absolute inset-0" />
                          {similarProduct.name}
                        </Link>
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">{similarProduct.origin}</p>
                    </div>
                    <p className="text-sm font-medium text-gray-900">
                      {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: similarProduct.currency }).format(similarProduct.price)}
                    </p>
                  </div>
                </motion.div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailPage;
